/**
 * Lightweight audio feedback utility.
 * Uses Web Audio API to synthesize short UI sounds — works cross-platform
 * (iOS Safari, Android Chrome, desktop). Replaces haptic feedback which
 * only worked on Android (Vibration API not supported by WebKit).
 *
 * User can toggle off via ProfileSheet — persisted in localStorage.
 */
import { safeLocalStorage } from './safeStorage';

const STORAGE_KEY = 'dekko_audio_feedback';

let ctx: AudioContext | null = null;

/** Lazily create AudioContext; resume if suspended (iOS gesture requirement). */
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || (window as any).webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx.state !== 'closed' ? ctx : null;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function isAudioFeedbackEnabled(): boolean {
  const val = safeLocalStorage.getItem(STORAGE_KEY);
  return val === null ? true : val === '1';
}

export function setAudioFeedbackEnabled(enabled: boolean): void {
  safeLocalStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
}

/** Play a single synthesised tone. `delay` is seconds from now. */
function playTone(
  freq: number,
  duration: number,
  volume = 0.08,
  type: OscillatorType = 'sine',
  delay = 0,
): void {
  if (prefersReducedMotion() || !isAudioFeedbackEnabled()) return;
  const c = getCtx();
  if (!c) return;

  const t = c.currentTime + delay;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(t);
  osc.stop(t + duration);
}

/** Presets — same shape as the old HAPTIC object for easy migration. */
export const AUDIO = {
  /** Soft click — general UI interactions */
  tap: () => playTone(800, 0.025, 0.06),

  /** Camera-shutter snap — photo capture, edge snapping */
  shutter: () => {
    playTone(1200, 0.02, 0.08);
    playTone(800, 0.018, 0.05, 'sine', 0.02);
  },

  /** Ascending pair — recording/session begins */
  start: () => {
    playTone(523, 0.07, 0.08);
    playTone(659, 0.08, 0.08, 'sine', 0.07);
  },

  /** Descending pair — recording/session stops */
  stop: () => {
    playTone(659, 0.07, 0.08);
    playTone(523, 0.08, 0.08, 'sine', 0.07);
  },

  /** Major arpeggio — submission confirmed */
  success: () => {
    playTone(523, 0.06, 0.08);
    playTone(659, 0.06, 0.08, 'sine', 0.06);
    playTone(784, 0.10, 0.08, 'sine', 0.12);
  },

  /** Dark descending — error / failure */
  error: () => {
    playTone(330, 0.09, 0.10, 'triangle');
    playTone(277, 0.12, 0.10, 'triangle', 0.09);
  },
} as const;
